import React, { useEffect, useMemo } from "react";
import {
  DEFAULT_IMAGE_DURATION_SECONDS,
  TV_DISPLAY_FIT_MODES,
  TV_DISPLAY_ORIENTATIONS,
  TV_DISPLAY_TRANSITIONS,
  TV_MEDIA_TYPES,
  TV_TEXT_ALIGNMENTS,
  TV_TEXT_POSITIONS,
} from "../../services/tv-disply/tvDisplayConstants";
import "./TvPresentationPreviewCanvas.css";

export default function TvPresentationPreviewCanvas({
  playlist,
  item,
  muted = true,
  autoPlayVideo = false,
  playing = autoPlayVideo,
  videoRef = null,
  onVideoEnded = null,
  showNativeVideoControls = true,
}) {
  const presentation = playlist?.presentation || {};
  const media = item?.media || null;
  const overlay = item?.overlay || { enabled: false };

  const transitionClass = useMemo(
    () => getTransitionClass(presentation.transition),
    [presentation.transition]
  );

  const frameClass = [
    "tv-presentation-preview",
    presentation.orientation === TV_DISPLAY_ORIENTATIONS.PORTRAIT ? "is-portrait" : "is-landscape",
  ].join(" ");

  useEffect(() => {
    const element = videoRef?.current;
    if (!element || media?.type !== TV_MEDIA_TYPES.VIDEO) return;

    if (playing) {
      const playPromise = element.play?.();
      if (typeof playPromise?.catch === "function") {
        playPromise.catch(() => {});
      }
      return;
    }

    element.pause?.();
  }, [media?.id, media?.type, playing, videoRef]);

  return (
    <div className={frameClass} style={{ "--tv-preview-bg": presentation.backgroundColor || "#000000" }}>
      <div className="tv-presentation-preview-stage">
        <div className={`tv-presentation-preview-media ${transitionClass}`}>
          {media?.type === TV_MEDIA_TYPES.IMAGE && media?.downloadURL ? (
            <img
              src={media.downloadURL}
              alt={media.name || "Preview image"}
              style={{ objectFit: getObjectFit(presentation.fitMode) }}
            />
          ) : null}

          {media?.type === TV_MEDIA_TYPES.VIDEO && media?.downloadURL ? (
            <video
              key={media.id}
              ref={videoRef}
              className="tv-presentation-preview-video"
              src={media.downloadURL}
              controls={showNativeVideoControls}
              muted={muted}
              playsInline
              autoPlay={autoPlayVideo}
              preload="metadata"
              onEnded={onVideoEnded}
              style={{ objectFit: getObjectFit(presentation.fitMode) }}
            />
          ) : null}

          {!media ? (
            <div className="tv-presentation-preview-placeholder">Media unavailable</div>
          ) : null}
        </div>

        {presentation.showTextOverlay !== false && overlay.enabled ? (
          <div
            className={`tv-presentation-overlay ${getPositionClass(overlay.position)} ${getAlignmentClass(overlay.textAlign)} ${getTextAnimationClass(overlay.animation)}`}
            style={{
              color: overlay.textColor,
              "--tv-overlay-accent": overlay.accentColor,
              "--tv-overlay-bg": overlay.backgroundColor,
              "--tv-overlay-opacity": overlay.backgroundOpacity ?? 0.55,
            }}
          >
            <div className="tv-presentation-overlay-card">
              {overlay.badge ? <div className="tv-presentation-overlay-badge">{overlay.badge}</div> : null}
              {overlay.headline ? <div className="tv-presentation-overlay-headline">{overlay.headline}</div> : null}
              {overlay.subheadline ? <div className="tv-presentation-overlay-subheadline">{overlay.subheadline}</div> : null}
              {overlay.priceText ? <div className="tv-presentation-overlay-price">{overlay.priceText}</div> : null}
              {overlay.ctaText ? <div className="tv-presentation-overlay-cta">{overlay.ctaText}</div> : null}
            </div>
          </div>
        ) : null}

        <div className="tv-presentation-preview-caption">
          <span>{presentation.orientation || TV_DISPLAY_ORIENTATIONS.LANDSCAPE}</span>
          <span>{presentation.fitMode || TV_DISPLAY_FIT_MODES.CROP}</span>
          <span>{presentation.transition || TV_DISPLAY_TRANSITIONS.FADE}</span>
          <span>
            {media?.type === TV_MEDIA_TYPES.IMAGE
              ? `${item?.imageDurationSeconds || presentation.defaultImageDurationSeconds || DEFAULT_IMAGE_DURATION_SECONDS}s`
              : "Video"}
          </span>
        </div>
      </div>
    </div>
  );
}

function getObjectFit(fitMode) {
  if (fitMode === TV_DISPLAY_FIT_MODES.FIT) return "contain";
  if (fitMode === TV_DISPLAY_FIT_MODES.STRETCH) return "fill";
  return "cover";
}

function getTransitionClass(value) {
  switch (value) {
    case TV_DISPLAY_TRANSITIONS.SLIDE:
      return "is-slide";
    case TV_DISPLAY_TRANSITIONS.ZOOM:
      return "is-zoom";
    case TV_DISPLAY_TRANSITIONS.KEN_BURNS:
      return "is-ken-burns";
    case TV_DISPLAY_TRANSITIONS.SCALE_FADE:
      return "is-scale-fade";
    case TV_DISPLAY_TRANSITIONS.SOFT_FLASH:
      return "is-soft-flash";
    case TV_DISPLAY_TRANSITIONS.RANDOM_PREMIUM:
      return "is-random-premium";
    default:
      return "is-fade";
  }
}

function getPositionClass(value) {
  return `pos-${String(value || TV_TEXT_POSITIONS.BOTTOM_LEFT).toLowerCase()}`;
}

function getAlignmentClass(value) {
  if (value === TV_TEXT_ALIGNMENTS.CENTER) return "align-center";
  if (value === TV_TEXT_ALIGNMENTS.RIGHT) return "align-right";
  return "align-left";
}

function getTextAnimationClass(value) {
  return `anim-${String(value || "FADE_UP").toLowerCase()}`;
}
